import { useState, useEffect, useCallback } from 'react';

interface MicrophoneDevice {
  deviceId: string;
  label: string;
}

export function useMicrophoneDevices() {
  const [devices, setDevices] = useState<MicrophoneDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 獲取設備列表
  const refreshDevices = useCallback(async () => {
    // 安全檢測：在非 HTTPS 環境下，navigator.mediaDevices 可能未定義
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('瀏覽器安全限制：需要 HTTPS 連接才能訪問麥克風');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // 需要先請求權限才能獲取設備標籤
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop()); // 立即釋放

      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = allDevices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `麥克風 ${device.deviceId.slice(0, 8)}`,
        }));

      setDevices(audioInputs);

      // 如果尚未選擇設備，選擇第一個
      if (!selectedDeviceId && audioInputs.length > 0) {
        setSelectedDeviceId(audioInputs[0].deviceId);
      }
    } catch (err) {
      console.error('Failed to enumerate devices:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('麥克風權限被拒絕');
        } else {
          setError(`無法獲取設備列表: ${err.message}`);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedDeviceId]);

  // 選擇設備
  const selectDevice = useCallback((deviceId: string) => {
    setSelectedDeviceId(deviceId);
  }, []);

  // 初始載入和監聽設備變化
  useEffect(() => {
    refreshDevices();

    const handleDeviceChange = () => {
      refreshDevices();
    };

    // 安全檢測：確認支援 addEventListener
    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
      return () => {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      };
    }
  }, [refreshDevices]); // devices is not in dep array, so it might be stale.

  // New Effect: Detect list changes and auto-switch if a NEW device appeared
  // We need to track the "previous" list to know if a new one was added.
  const [prevDeviceIds, setPrevDeviceIds] = useState<string[]>([]);

  useEffect(() => {
    const currentIds = devices.map(d => d.deviceId);
    
    // Check if we have a new device (that wasn't in prevDeviceIds)
    // Only auto-switch if we moved from fewer devices to more devices, or just found a new ID.
    const newDevice = devices.find(d => !prevDeviceIds.includes(d.deviceId));
    
    // Avoid switching on initial mount (prev is empty) -> logic:
    // If prevDeviceIds is empty, it's likely first load. Don't switch unless we want to enforce default.
    // But refreshDevices already sets default if null.
    
    if (prevDeviceIds.length > 0 && newDevice) {
       console.log('New device detected, auto-switching:', newDevice.label);
       setSelectedDeviceId(newDevice.deviceId);
    }
    
    // Update reference if changed
    if (JSON.stringify(currentIds) !== JSON.stringify(prevDeviceIds)) {
        setPrevDeviceIds(currentIds);
    }
  }, [devices, prevDeviceIds]);

  // 獲取當前選中設備的標籤
  const selectedDeviceLabel = devices.find(d => d.deviceId === selectedDeviceId)?.label || '選擇麥克風';

  return {
    devices,
    selectedDeviceId,
    selectedDeviceLabel,
    selectDevice,
    refreshDevices,
    isLoading,
    error,
  };
}
